import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../FirebaseConfig';
import noImg from "../assets/ChatGPT Image May 4, 2025, 05_02_10 AM.png";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { sendEmail } from '../component/emailService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// EmployeeDashboard - Limited access for employees
const EmployeeDashboard = ({ isSigned, Employees, userEmail }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]); // Separate state for services
  const [listings, setListings] = useState([]); // Separate state for listings
  const [foodItems, setFoodItems] = useState([]); // New state for food items
  const [promotion, setPromotion] = useState([]);
  const [promoStatusFilter, setPromoStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Redirect non-employee users (assuming employees have specific emails or roles)
  useEffect(() => {
    // Note: Adjust this logic based on how you identify employees
    if (!Employees?.includes(userEmail)) { // Reuse adminEmails or create employeeEmails
      navigate('/');
      toast.error("You do not have access to this page.");
    }
  }, [Employees, userEmail, navigate]);

  // Redirect non-signed-in users
  useEffect(() => {
    if (!isSigned) {
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [isSigned, navigate]);

  // Main data fetching effect (excluding orders and users)
  useEffect(() => {
    const fetchData = async () => {
      try {
        let snapshot;
        let data;
        let ref;

        // Fetch data based on the active tab (no orders or users)
        if (activeTab === 'promotion') {
          ref = collection(db, 'promotions');
          let promoQuery = promoStatusFilter === 'all'
            ? ref
            : query(ref, where('status', '==', promoStatusFilter));
          snapshot = await getDocs(promoQuery);
          data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setPromotion(data);
        } else if (activeTab === 'service') {
          ref = collection(db, 'services');
          snapshot = await getDocs(ref);
          data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setServices(data);
        } else if (activeTab === 'listing') {
          ref = collection(db, 'listings');
          snapshot = await getDocs(ref);
          data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setListings(data);
        } else if (activeTab === 'food') { // New food tab
          ref = collection(db, 'foodItems');
          snapshot = await getDocs(ref);
          data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setFoodItems(data);
        } else { // Products (all, pending, approved)
          let productsQuery = collection(db, 'Products');
          if (activeTab === 'pending') {
            productsQuery = query(productsQuery, where('approved', '==', false));
          } else if (activeTab === 'approved') {
            productsQuery = query(productsQuery, where('approved', '==', true));
          }
          snapshot = await getDocs(productsQuery);
          data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setProducts(data);
        }
      } catch (err) {
        console.error('Fetch error:', err.message);
        toast.error(`Failed to fetch data for ${activeTab}.`);
      }
    };
    fetchData();
  }, [activeTab, promoStatusFilter]);


  // Handler for product actions (products, services, listings)
  const handleProductApprove = async (id, email) => {
    await updateDoc(doc(db, 'Products', id), { approved: true });
    setProducts(prev => prev.filter(p => p.id !== id));
    await sendEmail(email, 'Product Approved', 'Your product has been approved.');
    toast.success("Product approved!");
  };

  const handleProductReject = async (id, email) => {
    await updateDoc(doc(db, 'Products', id), { approved: false });
    setProducts(prev => prev.filter(p => p.id !== id));
    await sendEmail(email, 'Product Rejected', 'Your product has been rejected.');
    toast.info("Product rejected.");
  };

  const handleProductDelete = async (id, email) => {
    await deleteDoc(doc(db, 'Products', id));
    setProducts(prev => prev.filter(p => p.id !== id));
    await sendEmail(email, 'Product Deleted', 'Your product has been deleted.');
    toast.warn("Product deleted.");
  };
  
  // Handler for new food item actions
  const handleFoodApprove = async (id) => {
    await updateDoc(doc(db, 'foodItems', id), { approved: true });
    setFoodItems(prev => prev.filter(f => f.id !== id));
    toast.success("Food item approved!");
  };
  
  const handleFoodDelete = async (id) => {
    await deleteDoc(doc(db, 'foodItems', id));
    setFoodItems(prev => prev.filter(f => f.id !== id));
    toast.warn("Food item deleted.");
  };

  const handleApprovePromotion = async (id) => {
    try {
      const promoRef = doc(db, "promotions", id);
      await updateDoc(promoRef, { status: "approved" });
      setPromotion(prev =>
        prev.map(p =>
          p.id === id ? { ...p, status: "approved" } : p
        )
      );
      toast.success("Promotion approved!");
    } catch (error) {
      console.error("Error approving promotion:", error.message);
      toast.error("Failed to approve promotion.");
    }
  };

  const handleDeletePromotion = async (id) => {
    await deleteDoc(doc(db, 'promotions', id));
    setPromotion(prev => prev.filter(p => p.id !== id));
    toast.warn("Promotion deleted.");
  };

  const handleServiceDelete = async (id, email) => {
    await deleteDoc(doc(db, 'services', id));
    setServices(prev => prev.filter(p => p.id !== id));
    await sendEmail(email, 'Service Deleted', 'Your service has been deleted.');
    toast.warn("Service deleted.");
  };

  const handlePropertyDelete = async (id, email) => {
    await deleteDoc(doc(db, 'listings', id));
    setListings(prev => prev.filter(p => p.id !== id));
    await sendEmail(email, 'Property Deleted', 'Your listing has been deleted.');
    toast.warn("Listing deleted.");
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFoodItems = foodItems.filter(f =>
    f.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-gray-900 min-h-screen text-white font-sans p-4 md:p-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-7xl mx-auto">
        {/* Header and Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-green-400 mb-4 md:mb-0">Admin board2</h1>

          <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
            {/* Dropdown for tab navigation (removed users and orders) */}
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="bg-gray-800 text-white p-3 rounded-lg border border-gray-700 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="pending">Pending Products</option>
              <option value="approved">Approved Products</option>
              <option value="all">All Products</option>
              <option value="food">Food Items</option>
              <option value="service">Services</option>
              <option value="listing">Listings</option>
              <option value="promotion">Promotions</option>
            </select>
            
            <input
              type="text"
              placeholder="Search by name"
              className="p-3 rounded-lg bg-gray-800 text-white border border-gray-700 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-green-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/AdminVerify" className="bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition w-full md:w-auto text-center">
              Verification System
            </Link>
          </div>
        </div>

        {/* Dynamic Content based on the selected tab */}
        {/* Promotion Status Filter (only for promotions tab) */}
        {activeTab === 'promotion' && (
          <div className="mb-6 flex gap-4 items-center">
            <label className="text-gray-400 font-semibold">Filter by status:</label>
            <select
              className="px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700"
              value={promoStatusFilter}
              onChange={(e) => setPromoStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        )}
        
        {/* --- Promotions Section --- */}
        {activeTab === 'promotion' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {promotion.length === 0 ? (
              <p className="text-center text-gray-400 col-span-full">No promotions to display.</p>
            ) : (
              promotion.map((promo) => (
                <div key={promo.id} className="bg-white/10 text-white rounded-xl p-6 shadow-xl border border-white/20">
                  <h3 className="font-bold text-xl mb-2">{promo.caption || "No Title"}</h3>
                  <p className="text-gray-300"><strong>Amount:</strong> ₦{promo.amount}</p>
                  <p className="text-gray-300"><strong>Platform(s):</strong> {Array.isArray(promo.platforms) ? promo.platforms.join(', ') : 'N/A'}</p>
                  <p className="text-gray-300"><strong>Status:</strong> <span className={promo.status === 'approved' ? 'text-green-400' : 'text-yellow-400'}>{promo.status}</span></p>
                  <div className="flex gap-2 mt-4">
                    {promo.status !== 'approved' && (
                      <button onClick={() => handleApprovePromotion(promo.id)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition">Approve</button>
                    )}
                    <button onClick={() => handleDeletePromotion(promo.id)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition">Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}


          {activeTab === "food" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFoodItems.length === 0 ? (
                <p className="text-center text-gray-400 col-span-full">No food items to display.</p>
              ) : (
                filteredFoodItems.map(food => (
                  <div key={food.id} className="bg-white/10 text-white rounded-xl p-6 shadow-xl border border-white/20">
                    <h3 className="font-bold text-xl mb-2">{food.name}</h3>
                    {food.imageUrl && <img src={food.imageUrl} alt={food.name} className="w-full h-48 object-cover rounded-lg mb-2" />}
                    <p className="text-gray-300"><strong>Price:</strong> ₦{food.price}</p>
                    <p className="text-gray-300"><strong>Prep Time:</strong> {food.time}</p>
                    <p className="text-gray-300"><strong>Approved:</strong> {food.approved ? 'Yes' : 'No'}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button onClick={() => handleFoodApprove(food.id)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition">Approve</button>
                      <button onClick={() => handleFoodDelete(food.id)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition">Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        {/* --- Services Section --- */}
        {activeTab === 'service' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.length === 0 ? (
              <p className="text-center text-gray-400 col-span-full">No services to display.</p>
            ) : (
              services.map(service => (
                <div key={service.id} className="bg-white/10 text-white rounded-xl p-6 shadow-xl border border-white/20">
                  <h3 className="font-bold text-xl mb-2">{service.name}</h3>
                  <p className="text-gray-300"><strong>Title:</strong> {service.title}</p>
                  <p className="text-gray-300"><strong>Phone:</strong> {service.phone || 'N/A'}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => handleServiceDelete(service.id, service.email)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition">Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* --- Listings Section --- */}
        {activeTab === 'listing' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.length === 0 ? (
              <p className="text-center text-gray-400 col-span-full">No listings to display.</p>
            ) : (
              listings.map(listing => (
                <div key={listing.id} className="bg-white/10 text-white rounded-xl p-6 shadow-xl border border-white/20">
                  <h3 className="font-bold text-xl mb-2">{listing.title || 'No Title'}</h3>
                  <p className="text-gray-300"><strong>Price:</strong> {listing.price || 'N/A'}</p>
                  <p className="text-gray-300"><strong>Description:</strong> {listing.description || 'Unknown'}</p>
                  <p className="text-gray-300"><strong>Location:</strong> {listing.location || 'Unknown'}</p>
                  <p className="text-gray-300"><strong>Amenities:</strong> {listing.amentities || 'Unknown'}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => handlePropertyDelete(listing.id, listing.email)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition">Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* --- Products Section (Default) --- */}
        {['all', 'pending', 'approved'].includes(activeTab) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.length === 0 ? (
              <p className="text-center text-gray-400 col-span-full">No products to display.</p>
            ) : (
              filteredProducts.map(product => (
                <div key={product.id} className="bg-white/10 text-white rounded-xl p-6 shadow-xl border border-white/20">
                  <h3 className="font-bold text-xl mb-2">{product.name}</h3>
                  <p className="text-gray-300"><strong>Description:</strong> {product.description}</p>
                  <p className="text-gray-300"><strong>Approved:</strong> {product.approved ? 'Yes' : 'No'}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {!product.approved && (
                      <button onClick={() => handleProductApprove(product.id, product.email)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition">Approve</button>
                    )}
                    <button onClick={() => handleProductReject(product.id, product.email)} className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition">Reject</button>
                    <button onClick={() => handleProductDelete(product.id, product.email)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition">Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;